import { z } from "zod";
import type { Journey, Quest } from "../earn";
import type { Partner } from "../partner";
import type { Objective } from "./objective";

export const playbookStatusSchema = z.enum(["ACTIVE", "COMPLETED", "DRAFT", "GENERATED", "ARCHIVED"]);
export type PlaybookStatus = z.infer<typeof playbookStatusSchema>;

export const playbookStrategySchema = z.enum([
  "ACQUISITION",
  "ACTIVATION",
  "RETENTION",
  "CROSS_CHAIN_CONQUESTING",
  "NEW_USER_ACQUISITION",
  "INCREASE_ENGAGEMENT",
  "LIQUIDITY_BUILDING",
]);
export type PlaybookStrategy = z.infer<typeof playbookStrategySchema>;

export type Playbook = {
  id: string;
  name: string | null;
  slug: string | null;
  description: string | null;
  budgetUsd: number | null;
  startsAt: Date | null;
  endsAt: Date | null;
  promptId: string | null;
  status: PlaybookStatus;
  strategy: PlaybookStrategy | null;
  createdById: string;
  updatedById: string;
  partnerId: string;
  createdAt: Date;
  updatedAt: Date;
  objectives?: Objective[];
  quests?: Quest[];
  journeys?: Journey[];
  partner?: Partner;
};

export const playbookSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  slug: z.string().nullable(),
  description: z.string().nullable(),
  budgetUsd: z.number().min(0).nullable(),
  startsAt: z.date().nullable(),
  endsAt: z.date().nullable(),
  promptId: z.string().nullable(),
  status: playbookStatusSchema,
  strategy: playbookStrategySchema.nullable(),
  createdById: z.string(),
  updatedById: z.string(),
  partnerId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  objectives: z.array(z.any()).optional(),
  quests: z.array(z.any()).optional(),
  journeys: z.array(z.any()).optional(),
  partner: z.any().optional(),
}) satisfies z.ZodType<Playbook>;

export const playbookUpdateSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    budgetUsd: z.number(),
    status: playbookStatusSchema,
    strategy: playbookStrategySchema,
    userId: z.string(),
    promptId: z.string().nullable(),
  })
  .partial();

export type PlaybookUpdate = z.infer<typeof playbookUpdateSchema>;

export function validateForGenerate(playbook: Playbook, objectives: Objective[]): void {
  if (playbook.status !== "DRAFT") {
    throw new Error("Playbook is not in DRAFT status");
  }
  if (playbook.strategy === null) {
    throw new Error("Playbook has no strategy");
  }
  if (playbook.budgetUsd === null || playbook.budgetUsd <= 0) {
    throw new Error("Playbook has no budget");
  }
  if (playbook.name === null || playbook.name.trim() === "") {
    throw new Error("Playbook has no name");
  }
  if (playbook.description === null || playbook.description.trim() === "") {
    throw new Error("Playbook has no description");
  }
  if (playbook.slug === null || playbook.slug.trim() === "") {
    throw new Error("Playbook has no slug");
  }
  if (objectives.length === 0) {
    throw new Error("Playbook has no objectives");
  }
  for (const objective of objectives) {
    if (objective.name === null || objective.name.trim() === "") {
      throw new Error("Objective has no name");
    }
  }
}
